import { useAppsStore } from '@/stores/apps.store';
import { useDashboardsStore } from '@/stores/dashboards.store';
import { useCoreEventsStore } from '@/stores/core-events.store';
import isArray from 'lodash/isArray';
import cloneDeep from 'lodash/cloneDeep';
import get from 'lodash/get';
import widgetCatalogSettings from '../widget-maps/widget-catalog-settings';
import { SET_DEFAULT_APP, SET_DEFAULT_APP_WITH_CORE_EVENTS } from '@/dashboards-v2/constants/default-app';
import widgetDomSettingsMap from '../widget-maps/widget-dom-settings.map';
import { v4 as uuid } from 'uuid';
import { AppsService } from '@/common/apps/apps.service.js';
import defaultWidgetOrder from '../constants/default-widget-order';
import DEFAULT_WIDGET_LAYOUT from '../constants/default-widget-layout.js';
import { HALF_WIDTH, FULL_WIDTH } from '@/dashboards-v2/constants/widget-dimensions.constants.js';
import { isDefaultSegment } from '@/utils/segments.js';
import { CURRENT_DASHBOARD_VERSION } from '@/types/dashboards/dashboard.types';
import i18nDateTimeFormatter from '@/utils/i18n/i18n-datetime-formatter';
import { i18n_t } from '@/utils/i18n';
import { activeSubHasPurchasedProductLine } from '@/data/flags/product-lines.js';
import { AEU_ANALYTICS_BLOCKED_WIDGETS } from '@/dashboards-v2/widget-maps/widget-aeu-blocked.util';
import { isFilterAppliedToWidget } from '@/dashboards-v2/components/dashboard-filter-bar/util/filter-modifiers';

import { useProductLinesStore } from '@/stores/product-lines.store';
import { useSubscriptionStore } from '@/stores/subscription.store';

const $t = i18n_t;

const allowedWidgetTypesForAdoptEndUser = [
    { type: 'activeVisitorsHourlyChart' },
    { type: 'browserUsageChart' },
    { type: 'guideOverview' },
    { type: 'mostSeenGuides' },
    {
        type: 'perWeekChart',
        settings: { kind: 'visitor' },
    },
    {
        type: 'scatterChart',
        settings: { kind: 'visitor' },
    },
    { type: 'tagOverview' },
    {
        type: 'visitorOverview',
        settings: { kind: 'visitor' },
    },
];

export const BLADE_REFLOW_TIMEOUT = 200;

export const DASHBOARD_PUSH_DRAWER_SWITCH_MS = 400;

const PENDO_PAGE_PUSH_STYLE_PROPS = [
    'margin-right',
    'margin-left',
    'margin-top',
    'margin-bottom',
    'will-change',
    'transition-duration',
    'transition-delay',
    'transition-timing-function',
];

export function clearPendoPagePushStyles () {
    if (typeof document === 'undefined') return;
    const el = document.querySelector('.pendo-page');
    if (!el?.style) return;
    PENDO_PAGE_PUSH_STYLE_PROPS.forEach((prop) => el.style.removeProperty(prop));
}

export const SCROLL_TIMEOUT = 1000;

export const WIDGET_MANAGEMENT_ACTIONS = {
    ADDED: 'added',
    UPDATED: 'updated',
    REMOVED: 'removed',
    RESIZED: 'resized',
    COLLABORATED: 'collaborated',
};

export const WIDGET_CATALOG_FILTERS = [
    {
        prop: ['title'],
        value: '',
    },
    {
        prop: 'widget.categories',
        value: [],
        trackFilter: 'Widget Type',
    },
];

export const DASHBOARD_CREATE_LOCATION = {
    LEFT_NAV: 'dashboardLeftNav',
    LIST_PAGE: 'dashboardListPage',
    HOMEPAGE: 'homepage',
    GLOBAL_CREATE: 'globalCreate',
};

const { ADDED, UPDATED } = WIDGET_MANAGEMENT_ACTIONS;

export const dashboardOwnershipOptions = () => [
    {
        id: 'all',
        label: $t('dashboardsHelpers.ownership.All dashboards'),
    },
    {
        id: 'createdByMe',
        label: $t('dashboardsHelpers.ownership.Created by me'),
    },
    {
        id: 'sharedWithMe',
        label: $t('dashboardsHelpers.ownership.Shared with me'),
    },
];

export async function createNewV2Dashboard (location, skipDefaultWidgets = false) {
    let widgets = [];

    if (!useDashboardsStore().ownerDashboards.length) {
        await Promise.all([useProductLinesStore().loadAll(), useCoreEventsStore().loadAll()]);
        if (!skipDefaultWidgets) {
            widgets = getDefaultDashboardWidgets();
        }
    }

    const dashboard = await useDashboardsStore().createV2Dashboard({
        name: `New Dashboard ${i18nDateTimeFormatter.formatTimestampLabel()}`,
        definition: { dashboardVersion: CURRENT_DASHBOARD_VERSION },
        widgets,
    });

    widgets.forEach((widget) => trackWidgetAction(widget, ADDED, 'via dashboard create'));
    window.pendo.track('Dashboard Created', { location });

    return dashboard;
}

export function isInDashboardV2SegmentFlag () {
    return window.pendo.segmentFlags.includes('updatedDashboards');
}

export function isDashboardV2 (dashboard) {
    return !!dashboard.definition && !get(dashboard, 'options.modules.length') && isArray(dashboard.widgets);
}

export function getRouteForDashboard (dashboard) {
    return {
        name: 'Dashboard',
        params: {
            id: dashboard.id,
        },
    };
}

export function getDefaultDashboardWidgets () {
    return widgetCatalogSettings()
        .reduce((dashboardWidgets, widget) => {
            const showWidget =
                useSubscriptionStore().hasAccountLevelAnalyticsEnabled || widget.settings.kind !== 'account';
            const isAdoptEndUser = activeSubHasPurchasedProductLine('adoptEndUser');
            const isAnalyticsEnabled = activeSubHasPurchasedProductLine('analytics');

            const showWidgetForAdoptEndUser = !isAdoptEndUser ||
                allowedWidgetTypesForAdoptEndUser.some(
                    (aeuWidget) => {
                        if (aeuWidget.type !== widget.type) return false;
                        if (aeuWidget.settings?.kind) {
                            return aeuWidget.settings.kind === widget.settings.kind;
                        }
                        if (!isAnalyticsEnabled && AEU_ANALYTICS_BLOCKED_WIDGETS.includes(widget.id)) return false;

                        return true;
                    },
                );

            if (widget.isDefault && showWidget && showWidgetForAdoptEndUser) {
                const { name, settings, type: widgetType } = widget;
                let { height, width } = widgetDomSettingsMap[widget.type] || {};

                if (isAdoptEndUser && widgetType === 'visitorOverview') {
                    width = FULL_WIDTH;
                }

                dashboardWidgets.push({
                    id: uuid(),
                    name,
                    type: widgetType,
                    definition: populateDefaultWidgetSettings(cloneDeep(settings), name),
                    height,
                    width,
                });
            }

            return dashboardWidgets;
        }, [])
        .sort((a, b) => defaultWidgetOrder.indexOf(a.name) - defaultWidgetOrder.indexOf(b.name));
}

export function getAiAgentCatalogPresetComponentType (widgetOrConfig) {
    return (
        widgetOrConfig?.definition?.aiAgentComponentType
        ?? widgetOrConfig?.settings?.aiAgentComponentType
    );
}

export function getWidgetTemplate (widget) {
    return widgetCatalogSettings().find((template) => {
        if (widget.type === 'dwmChart') {
            // saved widgets carry kind in definition; catalog items carry it in settings
            return template.type === widget.type
                && get(template, 'settings.kind') === (get(widget, 'settings.kind') ?? get(widget, 'definition.kind'));
        }

        if (widget.type === 'aiAgentWidget') {
            const presetIdByComponentType = {
                feedbackChart: 'aiAgentFeedbackSummary',
                toolsUsedChart: 'aiAgentToolsUsed',
                modelsUsedChart: 'aiAgentModelsUsed',
                subagentsUsedChart: 'aiAgentSubagentsUsed',
            };
            const presetId = presetIdByComponentType[widget.definition?.aiAgentComponentType];

            if (presetId) {
                return template.type === widget.type && template.id === presetId;
            }

            return template.type === widget.type && template.id === 'aiAgent';
        }

        return template.type === widget.type;
    });
}

export function populateDefaultWidgetSettings (settings, widgetName) {
    const appsList = useAppsStore().list;
    const coreEventsByAppId = useCoreEventsStore().coreEventListByAppId;

    if (settings.appId === SET_DEFAULT_APP) {
        const app = appsList.find((app) => app.integrated) || appsList[0];
        settings.appId = AppsService.getAppAggIdsFromOption(app.id);
    } else if (settings.appId === SET_DEFAULT_APP_WITH_CORE_EVENTS) {
        const app = appsList.find((app) => app.integrated && !!coreEventsByAppId(app.id).length) || appsList[0];
        settings.appId = AppsService.getAppAggIdsFromOption(app.id);
    } else if (isArray(settings.appId) && settings.appId.includes(SET_DEFAULT_APP)) {
        const app = appsList.find((app) => app.integrated) || appsList[0];
        settings.appId = [AppsService.getAppAggIdsFromOption(app.id)];
    }

    settings.layout = DEFAULT_WIDGET_LAYOUT[widgetName];

    return settings;
}

export function trackWidgetAction (widget, action, comment = '', extraProps = {}) {
    const actionMessage = (comment ? `${action} ${comment}` : action).trim();
    const segmentId = get(widget, 'definition.segmentId');
    const kind = get(widget, 'definition.kind');
    const period = get(widget, 'definition.period');
    const dateRange =
        get(widget, 'definition.dateRange.type', false) ||
        get(widget, 'definition.dateRange.timeSeries.config.type', null);
    const tags = get(widget, 'definition.tags', null);
    const totalEvents = tags?.length;
    const totalFeatures = tags?.filter((tag) => tag?.featureId).length;
    const totalPages = tags?.filter((tag) => tag?.pageId).length;
    const totalTrackTypes = tags?.filter((tag) => tag?.trackTypeId).length;
    const tagType = get(widget, 'definition.tagType');
    const eventType = get(widget, 'definition.eventType');
    const usesCoreEvents = get(widget, 'definition.useCoreEvents');
    const retentionFrequency = get(widget, 'definition.frequency');
    const retentionFirstUse = get(widget, 'definition.eventFirstUsedWithin');
    const retentionDateRange = get(widget, 'definition.frequencyTimespan');
    const templateCategories = getWidgetTemplate(widget)?.categories;
    const categories = Array.isArray(templateCategories) ? templateCategories.join(', ') : '';

    let { name } = widget;
    if (widget.type === 'aiAgentWidget') {
        name = widget.definition?.aiAgentComponentType;
    }

    window.pendo.track('Widget Management', {
        name,
        typeId: widget.type,
        categories,
        action: actionMessage,
        segmentId,
        isDefaultSegment: segmentId ? isDefaultSegment(segmentId) || segmentId.includes('_PENDO_DEFAULT_') : null,
        dateRange: dateRange || retentionDateRange,
        kind,
        frequency: period || retentionFrequency,
        firstUsePeriod: retentionFirstUse,
        tagType,
        eventType,
        totalEvents,
        totalFeatures,
        totalPages,
        totalTrackTypes,
        usesCoreEvents,
        ...extraProps,
    });

    if (widget.type === 'htmlEmbed' && action === ADDED) {
        window.pendo.track('embed_widget_add', {
            name: widget.name,
            source: widget.definition.src,
            uuid: widget.id,
        });
    }

    if (widget.type === 'richTextWidget' && [ADDED, UPDATED].includes(action)) {
        window.pendo.track('Rich Text Widget Update', {
            char_count: widget.definition.charCount,
            uuid: widget.id,
        });
    }
}

export function trackAddTemplate (template, isNewDashboard, subType, mobileProperties) {
    const { selectedTemplate, activeGroups, hasHelperText, enabledOptionalFields } = template;
    window.pendo.track('Dashboard Template Saved', {
        name: selectedTemplate,
        subscriptionType: subType || null,
        activeGroups,
        hasHelperText,
        isNewDashboard,
        enabledOptionalFields,
        ...mobileProperties,
    });
}

export function canEditDashboard (dashboard) {
    return ['owner', 'edit'].includes(get(dashboard, 'userDetails.permission'));
}

export function getWidgetWidth (widget) {
    return widget?.width || widgetDomSettingsMap[widget.type]?.width;
}

export function getWidgetWidthClass (widget) {
    return getWidgetWidth(widget) === HALF_WIDTH ? 'half-width' : 'full-width';
}

export function sortWidgets (a, b) {
    const sameDisabledState = a.disabled === b.disabled;
    const aDisabledOnly = !sameDisabledState && a.disabled;
    const bDisabledOnly = !sameDisabledState && b.disabled;
    if (bDisabledOnly || (sameDisabledState && a.title < b.title)) {
        return -1;
    }
    if (aDisabledOnly || (sameDisabledState && a.title > b.title)) {
        return 1;
    }

    return 0;
}

export function widgetSupportsFilter (widget, filterProp) {
    const { type, definition } = widget;
    if (['goalProgress', 'funnelReportChart', 'pathReportChart'].includes(type)) {
        return false;
    }
    if (['aiAgentWidget', 'customReportChart'].includes(type)) {
        return ['segmentId', 'account', 'dateRange'].includes(filterProp);
    }
    if (type === 'retentionReportChart') {
        return ['segmentId', 'account', 'dateRange', 'appId'].includes(filterProp);
    }

    // The per-type rules above are restrictive and stay in front; ownership and opt-in
    // are shared with widget-wrapper so the two can't drift.
    return isFilterAppliedToWidget(filterProp, type, definition);
}
